import { AxiosError } from 'axios';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import {
  EditClausesAction,
  EditClausesData,
} from '~/features/document-bulk-actions/EditClausesAction';
import { BulkClauseEditType } from '~/features/document-bulk-actions/types';
import { api } from '~/redux/api/createApi';

import { BaseBulkActionProps } from '../types/bulk-actions';
import { useDeleteConfirmationModal } from './hooks/useDeleteConfirmationModal';

interface Props extends BaseBulkActionProps {
  onHide: () => void;
  selectedCount: number;
}

export const EditClausesActionPanel = ({
  selectedDocumentIds = [],
  isAllSelected,
  onActionCompleted,
  query = {},
  selectedCount,
  searchAPI,
  onHide,
}: Props) => {
  const { toast } = useToast();
  const intl = useIntl();
  const [bulkEditClause, { isLoading }] =
    api.endpoints.searchV2BulkEditClause.useMutation();

  const [editClausesData, setEditClausesData] = useState<
    EditClausesData | undefined
  >();
  const {
    actionType,
    isAllClausesSelected = false,
    clausesNames = [],
    clausesNewValues,
  } = editClausesData ?? {};

  const genericErrorMessage = intl.formatMessage({
    id: 'EditClausesActionPanel.editClausesErrorMessage',
    defaultMessage: 'An error occurred while editing clauses.',
    description: 'Generic error message for clause editing failures',
  });

  const save = async () => {
    if (actionType) {
      try {
        await bulkEditClause({
          documentIds: !isAllSelected ? selectedDocumentIds : undefined,
          type: getActionTypePayload(actionType, isAllClausesSelected),
          query: isAllSelected ? query : undefined,
          searchAPI,
          clauseNames: actionType === 'delete' ? clausesNames : undefined,
          clauseAdditions: actionType === 'add' ? clausesNewValues : undefined,
        }).unwrap();
        toast({
          status: 'inactive',
          ...getSuccessToastMessage(
            actionType!,
            isAllClausesSelected,
            clausesNames.length,
            intl,
          ),
        });
        onActionCompleted?.();
      } catch (error) {
        const axiosError = (
          error as AxiosError<{
            detail: string;
            block_specific_clauses: boolean;
          }>
        ).response;
        let message: string;
        let isDangerStatus = true;
        if (axiosError?.status === HttpStatusCodeType.BadRequest) {
          if ('block_all_clauses' in axiosError?.data) {
            message = intl.formatMessage({
              id: 'EditClausesActionPanel.blockAllClausesMessage',
              defaultMessage:
                'Currently processing other bulk actions on clauses; additional actions are blocked until completion. Please try again in a few minutes.',
              description:
                'Error message when all clause actions are blocked due to processing',
            });
          } else if ('block_specific_clauses' in axiosError?.data) {
            isDangerStatus = false;
            message = intl.formatMessage({
              id: 'EditClausesActionPanel.blockSpecificClausesMessage',
              defaultMessage:
                'Currently processing other bulk actions on selected clauses; additional actions are blocked until completion. You can choose different clauses or try again in a few minutes.',
              description:
                'Warning message when specific clause actions are blocked due to processing',
            });
          } else {
            message = intl.formatMessage({
              id: 'EditClausesActionPanel.malformedRequestMessage',
              defaultMessage:
                'An error occured when trying to edit clauses. The request is malformed',
              description: 'Error message for malformed edit clauses request',
            });
          }
        } else {
          message = axiosError?.data.detail || genericErrorMessage;
        }
        toast({
          status: isDangerStatus ? 'danger' : 'warning',
          message: message ?? genericErrorMessage,
        });
      }
    }
  };

  const [deleteModal, showDeleteModal] = useDeleteConfirmationModal({
    entity: 'clauses',
    selectedCount,
    entityNames: clausesNames,
    isAllSelected: isAllClausesSelected,
    onDeleteClick: save,
    isDeleteActionLoading: isLoading,
  });

  const handleOnSaveClicked = () => {
    if (actionType === 'delete') {
      showDeleteModal();
    } else {
      save();
    }
  };

  const isSaveButtonDisabled = useMemo(() => {
    const areClausesEmpty = !clausesNames?.length && !isAllClausesSelected;
    const areClauseValuesEmpty =
      !clausesNewValues ||
      Object.values(clausesNewValues).some(
        (newClauseValue) => !newClauseValue.length,
      );
    return (
      !actionType ||
      areClausesEmpty ||
      (actionType === 'add' && areClauseValuesEmpty)
    );
  }, [actionType, clausesNames, isAllClausesSelected, clausesNewValues]);

  const footer = {
    actions: [
      {
        text: intl.formatMessage({
          id: 'EditClausesActionPanel.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text for edit clauses action',
        }),
        onClick: handleOnSaveClicked,
        level: 'primary' as const,
        disabled: isSaveButtonDisabled,
        isLoading,
      },
    ],
  };

  return (
    <>
      <Panel
        title={intl.formatMessage({
          id: 'EditClausesActionPanel.title',
          defaultMessage: 'Edit Clauses',
          description: 'Title for edit clauses panel',
        })}
        enableBackdrop
        footer={footer}
        width="m"
        hidden={{ isHidden: false, onHide }}
        position="right"
      >
        <Layout preset="sections">
          <Markdown
            text={intl.formatMessage(
              {
                id: 'EditClausesActionPanel.editingDescription',
                defaultMessage:
                  'Editing is limited to the documents and clauses you have permission to edit. Specify which clauses and values to edit across the **{selectedCount}** selected document(s) and their duplicate(s).',
                description:
                  'Description text explaining editing limitations and scope. Example: selectedCount = 25',
              },
              { selectedCount },
            )}
          />
          <EditClausesAction onChange={setEditClausesData} />
        </Layout>
      </Panel>
      {deleteModal}
    </>
  );
};

const getActionTypePayload = (
  actionType: EditClausesData['actionType'],
  isAllClausesSelected: boolean,
): BulkClauseEditType => {
  if (actionType === 'delete' && isAllClausesSelected) {
    return 'delete_all';
  }

  return actionType === 'delete' ? 'delete' : 'add';
};

const getSuccessToastMessage = (
  actionType: EditClausesData['actionType'],
  isAllClausesSelected: boolean,
  numberOfClauses: number,
  intl: ReturnType<typeof useIntl>,
): { title: string; message: string } => {
  let title;
  if (actionType === 'delete') {
    if (isAllClausesSelected) {
      title = intl.formatMessage({
        id: 'EditClausesActionPanel.deletingAllClauseValuesTitle',
        defaultMessage: 'Deleting all clause values',
        description: 'Toast title when deleting all clause values',
      });
    } else {
      title = intl.formatMessage({
        id: 'EditClausesActionPanel.deletingClauseValuesTitle',
        defaultMessage: 'Deleting clause values',
        description: 'Toast title when deleting specific clause values',
      });
    }
  } else {
    title = intl.formatMessage(
      {
        id: 'EditClausesActionPanel.updatingClausesTitle',
        defaultMessage:
          'Updating text for {numberOfClauses} {numberOfClauses, plural, one {clause} other {clauses}}',
        description:
          'Toast title when updating clause text with pluralization. Example: numberOfClauses = 3',
      },
      { numberOfClauses },
    );
  }

  return {
    title,
    message: intl.formatMessage({
      id: 'EditClausesActionPanel.browseAwayMessage',
      defaultMessage: 'You can browse away.',
      description:
        'Toast message indicating user can navigate away during processing',
    }),
  };
};
